import { useEffect, useState } from 'react';
import { db, storage } from './firebaseConfig';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  setDoc
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import Formulario from './Formulario';
import ChatAdmin from './ChatAdmin';

function AdminPanel({ isAdmin, user }) {
  const [activeTab, setActiveTab] = useState('perfiles');
  const [chatUsers, setChatUsers] = useState([]);
  const [selectedChatUser, setSelectedChatUser] = useState(null);
  const [publicaciones, setPublicaciones] = useState([]);
  const [editingService, setEditingService] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);

  const obtenerPublicaciones = async () => {
    const snapshot = await getDocs(collection(db, 'services'));
    const docs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setPublicaciones(docs);
  };

  useEffect(() => {
    // Escuchar Estadísticas Diarias (últimos 30 días)
    const qStats = query(collection(db, 'stats_daily'), orderBy('date', 'desc'), limit(30));
    const unsubscribeStats = onSnapshot(qStats, (snapshot) => {
      const stats = snapshot.docs.map(doc => ({
        ...doc.data(),
        date: doc.id
      }));
      // Invertir para mostrar cronológicamente en gráfico (izquierda antiguo -> derecha nuevo)
      setDailyStats(stats.reverse());
    });
    return () => unsubscribeStats();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'admin_chats'), orderBy('timestamp', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const usersMap = {};
      // Procesar de más nuevo a más viejo
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const chatId = data.chatId;
        // Si el timestamp es null (pendiente), lo tratamos como "ahora" para que aparezca arriba
        const msgTime = data.timestamp ? data.timestamp.toMillis() : Date.now();

        // El nombre de la conversación debe ser el del cliente (no el del admin si es quien respondió último)
        // Guardamos el nombre del primer mensaje que no sea admin, o cualquiera si no hay más
        const isClientMessage = !data.isAdmin;

        if (!usersMap[chatId]) {
          usersMap[chatId] = {
            userId: chatId,
            userName: isClientMessage ? data.senderName : 'Usuario',
            lastMessage: (data.isAdmin ? 'Tu: ' : '') + data.text,
            timestamp: data.timestamp,
            _sortTime: msgTime,
            isUnread: isClientMessage // Es no leído si el primer mensaje detectado es de cliente
          };
        } else {
          // Si el mensaje actual es más nuevo, actualizamos el texto y el tiempo
          if (msgTime > usersMap[chatId]._sortTime) {
            usersMap[chatId].lastMessage = (data.isAdmin ? 'Tu: ' : '') + data.text;
            usersMap[chatId].timestamp = data.timestamp;
            usersMap[chatId]._sortTime = msgTime;
            usersMap[chatId].isUnread = !data.isAdmin; // Es no leído si el último mensaje es de cliente
          }
          // Si este mensaje es del cliente y aún no tenemos un nombre real, lo actualizamos
          if (isClientMessage && usersMap[chatId].userName === 'Usuario') {
            usersMap[chatId].userName = data.senderName;
          }
        }
      });
      // Ordenar la lista final por tiempo real
      const sorted = Object.values(usersMap).sort((a, b) => b._sortTime - a._sortTime);
      setChatUsers(sorted);
    }, (err) => {
      console.error("Inbox listener error:", err);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'services'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPublicaciones(docs);
    });
    return () => unsubscribe();
  }, []);

  const toggleVisibilidad = async (pub) => {
    const nuevoEstado = !pub.visible;
    await updateDoc(doc(db, 'services', pub.id), {
      visible: nuevoEstado,
      aprobado: true // Al hacerlo visible, asumimos que está aprobado
    });
    setPublicaciones(prev =>
      prev.map(p => p.id === pub.id ? { ...p, visible: nuevoEstado, aprobado: true } : p)
    );
  };

  const aprobarPerfil = async (pub) => {
    await updateDoc(doc(db, 'services', pub.id), { aprobado: true });
    setPublicaciones(prev =>
      prev.map(p => p.id === pub.id ? { ...p, aprobado: true } : p)
    );
  };

  const cambiarMembresia = async (pub, nuevaMembresia) => {
    await updateDoc(doc(db, 'services', pub.id), { membresia: nuevaMembresia });
    setPublicaciones(prev =>
      prev.map(p => p.id === pub.id ? { ...p, membresia: nuevaMembresia } : p)
    );
  };

  const eliminarPublicacion = async (pub) => {
    if (!window.confirm("¿Seguro que quieres eliminar este perfil para siempre?")) return;

    if (pub.imageUrls && pub.imageUrls.length > 0) {
      for (const url of pub.imageUrls) {
        try {
          const imagePath = new URL(url).pathname.split('/o/')[1].split('?')[0];
          const decodedPath = decodeURIComponent(imagePath);
          await deleteObject(ref(storage, decodedPath));
        } catch (e) {
          console.warn("Error borrando imagen, quizá ya no existía:", e);
        }
      }
    }

    await deleteDoc(doc(db, 'services', pub.id));
    setPublicaciones(prev => prev.filter(p => p.id !== pub.id));
  };

  // Si estamos editando, mostramos el Formulario con los datos
  if (editingService) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => setEditingService(null)}
          className="mb-4 text-white underline"
        >
          &larr; Volver al Panel
        </button>
        <h2 className="text-2xl font-bold mb-4 text-white">Editando a: {editingService.nombre}</h2>

        <Formulario
          user={{ uid: editingService.userId, displayName: editingService.nombreUsuario }} // Mock user para pasar validaciones
          initialData={editingService}
          onCancel={() => setEditingService(null)}
          onSuccess={() => {
            setEditingService(null);
            obtenerPublicaciones(); // Recargar datos
          }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 font-josefin">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
        <div>
          <h2 className="text-3xl font-black text-white uppercase tracking-tight">Admin Control Center</h2>
          <p className="text-gray-500 text-sm mt-1">Gestiona perfiles, membresías y comunicaciones.</p>
        </div>

        <div className="flex bg-neutral-900 p-1 rounded-xl border border-neutral-800 self-start">
          <button
            onClick={() => setActiveTab('perfiles')}
            className={`px-6 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'perfiles' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
          >
            PERFILES
          </button>
          <button
            onClick={() => setActiveTab('inbox')}
            className={`px-6 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${activeTab === 'inbox' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
          >
            INBOX
            {chatUsers.filter(u => u.isUnread).length > 0 && (
              <span className="bg-red-500 text-[9px] w-4 h-4 flex items-center justify-center rounded-full animate-bounce">
                {chatUsers.filter(u => u.isUnread).length}
              </span>
            )}
          </button>
        </div>
      </div>

      {activeTab === 'perfiles' ? (
        <>
          <div className="overflow-x-auto bg-neutral-900 rounded-2xl shadow-2xl border border-neutral-800">
            <table className="min-w-full text-left text-sm text-gray-300">
              <thead className="bg-neutral-800 text-white uppercase tracking-wider font-semibold">
                <tr>
                  <th className="px-6 py-4">Perfil</th>
                  <th className="px-6 py-4">Estadísticas</th>
                  <th className="px-6 py-4">Detalles</th>
                  <th className="px-6 py-4">Contacto</th>
                  <th className="px-6 py-4 text-center">Nivel</th>
                  <th className="px-6 py-4 text-center">Estado</th>
                  <th className="px-6 py-4 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {publicaciones.map((pub) => (
                  <tr key={pub.id} className="hover:bg-neutral-800/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {pub.portadaUrl && (
                          <img src={pub.portadaUrl} alt="avatar" className="h-10 w-10 rounded-full object-cover border border-white/20" />
                        )}
                        <div>
                          <p className="font-bold text-white text-base flex items-center gap-2">
                            {pub.nombre}
                            {!pub.aprobado && <span className="bg-red-500 text-[8px] px-1.5 py-0.5 rounded animate-pulse">SOLICITUD</span>}
                          </p>
                          <p className="text-xs text-gray-500">@{pub.nombreUsuario || 'Anon'}</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-xs space-y-1">
                        <p className="text-gray-300">👁️ Vistas: <span className="font-bold text-white">{pub.vistas || 0}</span></p>
                        <p className="text-gray-300">📲 Clics WA: <span className="font-bold text-green-400">{pub.whatsappClicks || 0}</span></p>
                      </div>
                    </td>

                    <td className="px-6 py-4 space-y-1">
                      <p><span className="text-amber-500">Rol:</span> {pub.rol || '--'}</p>
                      <p><span className="text-blue-400">Ciudad:</span> {pub.ciudad || '--'}</p>
                      <p className="text-xs text-gray-500">{pub.edad} años • {pub.altura || '?'}m</p>
                    </td>

                    <td className="px-6 py-4">
                      {pub.whatsapp ? (
                        <a
                          href={`https://wa.me/${pub.whatsapp.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-400 hover:text-green-300 flex items-center gap-1 font-mono"
                        >
                          {pub.whatsapp} ↗
                        </a>
                      ) : <span className="text-gray-600">--</span>}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <select
                        value={pub.membresia || 'Gratuito'}
                        onChange={(e) => cambiarMembresia(pub, e.target.value)}
                        className={`text-xs font-bold rounded px-2 py-1 bg-black border ${pub.membresia === 'VIP' ? 'text-yellow-500 border-yellow-500/50' :
                          pub.membresia === 'Semi VIP' ? 'text-blue-400 border-blue-400/50' :
                            'text-gray-400 border-gray-600'
                          }`}
                      >
                        <option value="Gratuito">GRATUITO</option>
                        <option value="Semi VIP">SEMI VIP</option>
                        <option value="VIP">VIP</option>
                      </select>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => toggleVisibilidad(pub)}
                        className={`px-3 py-1 rounded-full text-xs font-bold border ${pub.visible
                          ? 'bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30'
                          : 'bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30'
                          }`}
                      >
                        {pub.visible ? 'VISIBLE' : 'OCULTO'}
                      </button>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {!pub.aprobado && (
                          <button
                            onClick={() => aprobarPerfil(pub)}
                            className="bg-green-600 hover:bg-green-500 text-white text-xs font-bold px-3 py-1 rounded transition-colors"
                          >
                            Aprobar
                          </button>
                        )}
                        <button
                          onClick={() => toggleVisibilidad(pub)}
                          className={`${pub.visible ? 'bg-yellow-600' : 'bg-neutral-700'
                            } text-white text-xs px-3 py-1 rounded font-bold hover:brightness-110 transition-all`}
                        >
                          {pub.visible ? 'Ocultar' : 'Publicar'}
                        </button>
                        <button
                          onClick={() => {
                            setEditingService(pub);
                            setActiveTab('perfiles'); // Asegurar que estamos en perfiles al editar
                          }}
                          className="bg-blue-600 hover:bg-blue-500 text-white text-xs px-3 py-1 rounded font-bold"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => eliminarPublicacion(pub)}
                          className="bg-red-900/50 hover:bg-red-600 text-red-200 text-xs px-3 py-1 rounded font-bold transition-colors"
                        >
                          Borrar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {publicaciones.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      No hay perfiles registrados aún.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-8 bg-neutral-900 rounded-2xl shadow-2xl border border-neutral-800 p-6">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              📊 Tráfico del Sitio <span className="text-sm font-normal text-gray-500">(Últimos 30 días)</span>
            </h3>

            <div className="h-64 flex items-stretch gap-1 justify-between">
              {(() => {
                // Generar últimos 30 días cronológicamente
                const days = [...Array(30)].map((_, i) => {
                  const d = new Date();
                  d.setDate(d.getDate() - i);
                  return d.toISOString().split('T')[0];
                }).reverse();

                const maxVisits = Math.max(...dailyStats.map(s => s.visits || 0), 10);

                return days.map((dateStr, idx) => {
                  const stat = dailyStats.find(s => s.date === dateStr) || { visits: 0 };
                  const heightPercent = Math.max(((stat.visits || 0) / maxVisits) * 100, 5); // Base 5%
                  const isToday = idx === 29;

                  return (
                    <div key={dateStr} className="flex-1 flex flex-col items-center justify-end group relative h-full">
                      {/* Tooltip */}
                      <div className="absolute bottom-full mb-2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap z-10 pointer-events-none border border-white/20 shadow-xl">
                        <span className="font-bold">{dateStr}</span>: {stat.visits} visitas
                      </div>

                      {/* Barra */}
                      <div
                        className={`w-full rounded-t transition-all relative ${isToday ? 'bg-green-500 shadow-[0_0_15px_rgba(34,197,94,0.5)] animate-pulse' : 'bg-blue-600/40 hover:bg-blue-500'}`}
                        style={{ height: `${heightPercent}%` }}
                      >
                      </div>

                      {/* Eje X (Mostrar cada 3 días) */}
                      <span className={`text-[8px] mt-2 rotate-45 transform origin-left truncate w-full text-center absolute top-full ${isToday ? 'text-green-400 font-bold' : 'text-gray-600'}`}>
                        {idx % 3 === 0 || isToday ? dateStr.slice(5) : ''}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LISTA DE CONVERSACIONES */}
          <div className="lg:col-span-1 bg-neutral-900 rounded-2xl border border-neutral-800 overflow-hidden flex flex-col h-[600px] shadow-2xl">
            <div className="p-4 border-b border-neutral-800 bg-neutral-800/30">
              <h3 className="font-bold text-white uppercase text-xs tracking-widest">Conversaciones</h3>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-neutral-800/50">
              {chatUsers.length === 0 && (
                <p className="text-center text-gray-500 text-sm py-10">No hay mensajes aún.</p>
              )}
              {chatUsers.map(chat => (
                <button
                  key={chat.userId}
                  onClick={() => setSelectedChatUser(chat)}
                  className={`w-full p-4 text-left transition-all hover:bg-neutral-800 border-l-4 ${selectedChatUser?.userId === chat.userId ? 'border-blue-500 bg-neutral-800' : 'border-transparent'}`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <p className="font-bold text-white text-sm truncate">{chat.userName || 'Usuario'}</p>
                    <span className="text-[9px] text-gray-500 uppercase">
                      {chat.timestamp ? new Date(chat.timestamp?.toDate()).toLocaleDateString() : '--'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 truncate opacity-80">{chat.lastMessage}</p>
                </button>
              ))}
            </div>
          </div>

          {/* CHAT ACTIVO */}
          <div className="lg:col-span-2 bg-neutral-900 rounded-2xl border border-neutral-800 overflow-hidden flex flex-col h-[600px] shadow-2xl relative">
            {selectedChatUser ? (
              <div className="h-full flex flex-col">
                <div className="p-4 border-b border-neutral-800 flex justify-between items-center bg-blue-600/5">
                  <p className="font-black text-blue-400 uppercase text-xs tracking-tighter">Chat con: {selectedChatUser.userName}</p>
                  <button onClick={() => setSelectedChatUser(null)} className="text-[10px] text-gray-500 hover:text-white uppercase transition-colors">Cerrar Chat</button>
                </div>
                <div className="flex-1 overflow-hidden p-4">
                  <ChatAdmin adminMode={true} targetUserId={selectedChatUser.userId} user={user} />
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-600 p-10 text-center">
                <span className="text-5xl mb-4">💬</span>
                <p className="text-lg font-bold">Selecciona una conversación</p>
                <p className="text-sm italic">Los mensajes de los usuarios pidiendo VIP o ayuda aparecerán aquí.</p>
              </div>
            )}
          </div>
        </div>
      )
      }
    </div >
  );
}

export default AdminPanel;
